import React, { useEffect, useState } from 'react';
import {
  FaSearch,
  FaTimes,
} from 'react-icons/fa';
import { Employee } from '../../../hooks/useEmployeeData';
import { pdebugError } from '../utils/plantafelDebug';
import { AvailableEmployee, EmployeeAssignment, DayInfo, Absence } from './leftPanelTypes';
import styles from './PlantafelLeftPanel.module.css';

interface LeftPanelEmployeesProps {
  isActive: boolean;
  assignedEmployeeMap?: Map<number, EmployeeAssignment>;
  employeeHoursMap?: Map<number, number>;
  onHighlightShift?: (shiftId: number) => void;
  absences?: Absence[];
  days?: DayInfo[];
  onAbsentEmployeeClick?: (employeeId: number) => void;
}

type HoursStatus = 'ok' | 'warning' | 'over';

const getStatusColors = (status: HoursStatus) => {
  switch (status) {
    case 'over':
      return { background: 'rgba(239, 68, 68, 0.2)', color: '#991b1b' };
    case 'warning':
      return { background: 'rgba(245, 158, 11, 0.2)', color: '#92400e' };
    case 'ok':
    default:
      return { background: 'rgba(16, 185, 129, 0.2)', color: '#065f46' };
  }
};

export function LeftPanelEmployees({
  isActive,
  assignedEmployeeMap,
  employeeHoursMap,
  onHighlightShift,
  absences = [],
  days = [],
  onAbsentEmployeeClick,
}: LeftPanelEmployeesProps): React.JSX.Element {
  const [employees, setEmployees] = useState<AvailableEmployee[]>([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [employeeSearch, setEmployeeSearch] = useState('');
  const [clickedEmployeeId, setClickedEmployeeId] = useState<number | null>(null);

  useEffect(() => {
    if (!isActive) {
      return;
    }

    const loadEmployees = async () => {
      setLoadingEmployees(true);
      try {
        const response = await import('../../../config/axios').then(m => m.default.get('/api/admin/employees/'));
        const allEmployees = Array.isArray(response.data)
          ? response.data
          : response.data?.employees || response.data?.results || [];
        const activeEmployees = allEmployees.filter((e: Employee) => e.is_active !== false);
        setEmployees(activeEmployees.map((e: Employee) => ({
          id: e.id,
          first_name: e.first_name,
          last_name: e.last_name,
          employee_number: e.employee_number,
          is_available: true,
          min_hours: e.min_hours || 10,
          photo_url: e.photo_url,
        })));
      } catch (err) {
        pdebugError('Failed to load employees:', err);
        setEmployees([]);
      } finally {
        setLoadingEmployees(false);
      }
    };

    loadEmployees();
  }, [isActive]);

  const searchLower = employeeSearch.toLowerCase();
  const filteredEmployees = employeeSearch
    ? employees.filter(e =>
        e.first_name.toLowerCase().includes(searchLower) ||
        e.last_name.toLowerCase().includes(searchLower) ||
        e.employee_number?.toLowerCase().includes(searchLower)
      )
    : employees;

  return (
    <>
      <div className={styles.searchBarContainer}>
        <div className={styles.searchBarInner}>
          <FaSearch size={10} color="#888" />
          <input
            type="text"
            placeholder="Mitarbeiter suchen..."
            value={employeeSearch}
            onChange={(e) => setEmployeeSearch(e.target.value)}
            className={styles.searchInput}
          />
          {employeeSearch && (
            <button
              onClick={() => setEmployeeSearch('')}
              className={styles.searchClearButton}
            >
              <FaTimes size={8} />
            </button>
          )}
        </div>
        <div className={styles.searchCount}>
          {filteredEmployees.length} von {employees.length} Mitarbeiter &bull; Drag auf Job
        </div>
      </div>

      <div className={styles.employeesList}>
        {loadingEmployees ? (
          <div className={styles.loadingStateSmall}>
            Lade Mitarbeiter...
          </div>
        ) : filteredEmployees.length === 0 ? (
          <div className={styles.loadingStateSmall}>
            {employeeSearch ? 'Keine Mitarbeiter gefunden' : 'Keine Mitarbeiter verfügbar'}
          </div>
        ) : (
          <div className={styles.employeesColumn}>
            {filteredEmployees
              .slice()
              .sort((a, b) => (a.first_name || '').localeCompare(b.first_name || ''))
              .map((emp) => {
              const isAvailable = emp.is_available !== false;
              const assignment = assignedEmployeeMap?.get(emp.id);
              const isAssigned = !!assignment;
              const isClicked = clickedEmployeeId === emp.id;
              const waColor = assignment?.workAreaColor || '#7c3aed';

              const employeeAbsences = absences.filter(a => {
                if (a.employee !== emp.id) return false;
                return days.some(day => {
                  const dayStart = new Date(`${day.dateStr}T00:00:00`).getTime();
                  const dayEnd = new Date(`${day.dateStr}T23:59:59`).getTime();
                  const absStart = new Date(a.start_datetime).getTime();
                  const absEnd = new Date(a.end_datetime).getTime();
                  return absStart <= dayEnd && absEnd >= dayStart;
                });
              });
              const hasApprovedAbsence = employeeAbsences.some(a => a.status === 'APPROVED');
              const hasRequestedAbsence = employeeAbsences.some(a => a.status === 'REQUESTED');
              const isAbsent = hasApprovedAbsence || hasRequestedAbsence;

              const assignedHours = employeeHoursMap?.get(emp.id) || 0;
              const weeklyHours = emp.min_hours || 10;
              const TOLERANCE = 3;
              let hoursStatus: HoursStatus = 'ok';
              if (assignedHours > weeklyHours + TOLERANCE) {
                hoursStatus = 'over';
              } else if (assignedHours > weeklyHours) {
                hoursStatus = 'warning';
              }
              const statusColors = getStatusColors(hoursStatus);

              const displayName = `${emp.first_name || ''} ${emp.last_name?.[0] || ''}.`;

              return (
                <div
                  key={emp.id}
                  draggable={isAvailable}
                  onClick={() => {
                    if (hasApprovedAbsence && onAbsentEmployeeClick) {
                      setClickedEmployeeId(emp.id);
                      onAbsentEmployeeClick(emp.id);
                      setTimeout(() => setClickedEmployeeId(null), 3500);
                    } else if (isAssigned && onHighlightShift) {
                      setClickedEmployeeId(emp.id);
                      onHighlightShift(assignment.shiftId);
                      setTimeout(() => setClickedEmployeeId(null), 1500);
                    }
                  }}
                  onDragStart={(e) => {
                    if (!isAvailable) {
                      e.preventDefault();
                      return;
                    }
                    e.dataTransfer.setData('employeeId', emp.id.toString());
                    e.dataTransfer.setData('employeeName', `${emp.first_name} ${emp.last_name}`);
                    e.dataTransfer.effectAllowed = 'move';
                  }}
                  className={styles.employeeRow}
                  style={{
                    background: isClicked
                      ? `linear-gradient(90deg, ${hasApprovedAbsence ? '#021a4a25' : waColor + '25'} 0%, ${hasApprovedAbsence ? '#021a4a05' : waColor + '05'} 100%)`
                      : undefined,
                    border: isClicked
                      ? `2px solid ${hasApprovedAbsence ? '#021a4a' : waColor}`
                      : undefined,
                    cursor: isAvailable ? 'grab' : 'not-allowed',
                    opacity: isAvailable ? 1 : 0.5,
                    boxShadow: isClicked ? `0 0 8px 2px ${hasApprovedAbsence ? 'rgba(2, 26, 74, 0.4)' : waColor + '60'}` : undefined,
                  }}
                  title={
                    hasApprovedAbsence
                      ? `${emp.first_name} ${emp.last_name} - Abwesend - Klick zum Anzeigen`
                      : isAssigned
                        ? `${emp.first_name} ${emp.last_name} → ${assignment.jobTitle}`
                        : `${emp.first_name} ${emp.last_name} - Drag auf Job`
                  }
                >
                  {emp.photo_url ? (
                    <img
                      src={emp.photo_url}
                      alt={displayName}
                      className={styles.employeeAvatar}
                      style={{
                        border: isAssigned ? `2px solid ${waColor}` : '2px solid transparent',
                      }}
                    />
                  ) : (
                    <span
                      className={styles.employeeInitials}
                      style={{
                        background: isAssigned ? waColor : '#5271A5',
                      }}
                    >
                      {emp.first_name?.[0]}{emp.last_name?.[0]}
                    </span>
                  )}

                  <div className={styles.employeeNameContainer}>
                    <span className={styles.employeeName}>
                      {displayName}
                    </span>
                    {hasApprovedAbsence && (
                      <span
                        className={styles.absenceBadge}
                        title="Genehmigte Abwesenheit"
                      >
                        ABS
                      </span>
                    )}
                  </div>

                  <span
                    className={styles.hoursBadge}
                    style={statusColors}
                  >
                    {assignedHours.toFixed(1)}/ {weeklyHours}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}
